import { LazyQueryExecFunction, OperationVariables } from '@apollo/client'
import { IconButton, Badge, TableSortLabel } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import React from 'react'

import { getTranslate } from '../../../localization'
import { InputData, LogsData, SortValue } from '../../../types/auditLogs'
import useStyles from './styles/AuditLogsFormListTableHead.styles'

type SortDir = 'ASC' | 'DESC'
type SortKey =
  | 'DATE'
  | 'USER'
  | 'ACTION'
  | 'CORRELATION'
  | 'CONTENT'
  | 'STATUS'
  | 'HASH'
  | 'RESULT'

interface TableCell {
  key: SortKey
  label: string
  sortable: boolean
}

interface AuditLogsFormListTableHeadDesktopProps {
  getAuditLogsData: LazyQueryExecFunction<
    { auditLogs: LogsData },
    OperationVariables
  >
  inputData: InputData
  setInputData: React.Dispatch<React.SetStateAction<InputData>>
  setPage: React.Dispatch<React.SetStateAction<number>>
  newLogsCount: number
  isRoleAdmin: boolean
}

const AuditLogsFormListTableHeadDesktop: React.FC<
  AuditLogsFormListTableHeadDesktopProps
> = ({
  getAuditLogsData,
  inputData,
  setInputData,
  setPage,
  newLogsCount,
  isRoleAdmin
}) => {
  const classes = useStyles()

  const [columnSortDir, setColumnSortDir] = React.useState<
    Record<SortKey, SortDir>
  >({
    DATE: 'DESC',
    USER: 'DESC',
    ACTION: 'DESC',
    CORRELATION: 'DESC',
    CONTENT: 'DESC',
    STATUS: 'DESC',
    HASH: 'DESC',
    RESULT: 'DESC'
  })

  const tableCells: TableCell[] = [
    { key: 'DATE', label: getTranslate('Date'), sortable: true },
    {
      key: 'CORRELATION',
      label: getTranslate('Correlation ID'),
      sortable: false
    },
    { key: 'USER', label: getTranslate('User'), sortable: true },
    { key: 'ACTION', label: getTranslate('Action'), sortable: true },
    { key: 'CONTENT', label: getTranslate('Content'), sortable: false },
    { key: 'STATUS', label: getTranslate('Status'), sortable: false },
    { key: 'HASH', label: getTranslate('Integrity Status'), sortable: false },
    { key: 'RESULT', label: getTranslate('Result'), sortable: false }
  ]

  const toggleSort = (key: SortKey): SortValue => {
    const currentDir = columnSortDir[key]
    const nextDir: SortDir = currentDir === 'DESC' ? 'ASC' : 'DESC'

    setColumnSortDir((prev) => ({
      ...prev,
      [key]: nextDir
    }))

    return `${key}_${nextDir}` as SortValue
  }

  return (
    <div className={[classes.root, isRoleAdmin && classes.adminRoot].join(' ')}>
      {tableCells
        .filter((cell) => isRoleAdmin || cell.key !== 'CORRELATION')
        .map((cell) => {
          return (
            <TableSortLabel
              key={cell.key}
              className={classes.tableSortLabel}
              active={cell.sortable}
              direction={columnSortDir[cell.key] === 'ASC' ? 'asc' : 'desc'}
              hideSortIcon
              onClick={() => {
                if (!cell.sortable) return
                const willChangePage = inputData.page !== 1
                const sortInput = {
                  ...inputData,
                  sort: toggleSort(cell.key),
                  page: 1,
                  requestID: ''
                }
                setPage(1)
                setInputData(sortInput)
                if (!willChangePage) {
                  getAuditLogsData({ variables: { input: sortInput } })
                }
              }}>
              {cell.label}
            </TableSortLabel>
          )
        })}
      <div>
        <IconButton
          aria-label={getTranslate('Refresh')}
          onClick={() => {
            const willChangePage = inputData.page !== 1
            setPage(1)
            const refreshInput = { ...inputData, page: 1, requestID: '' }
            setInputData(refreshInput)
            if (!willChangePage) {
              getAuditLogsData({ variables: { input: refreshInput } })
            }
          }}>
          <Badge badgeContent={newLogsCount} color="primary">
            <RefreshIcon />
          </Badge>
        </IconButton>
      </div>
    </div>
  )
}

export default AuditLogsFormListTableHeadDesktop
